import { useEffect, useRef, useState } from "react";
import { IdCard, ShieldCheck, ShieldAlert, Upload, Camera, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HospitalCombobox from "@/components/HospitalCombobox";
import SpecialtyCombobox from "@/components/SpecialtyCombobox";
import InsurancesEditor from "@/components/InsurancesEditor";

export default function DoctorProfileEdit() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    specialty: "", license_number: "", price: "", location: "", center_name: "", bio: "", photo_url: "",
    name: "", phone: "", email: "",
  });
  const [exequatur, setExequatur] = useState("");
  const [centers, setCenters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const p = user.doctor_profile || {};
    setForm({
      specialty: p.specialty || "", license_number: p.license_number || "", price: p.price ?? "",
      location: p.location || "", center_name: p.center_name || "", bio: p.bio || "", photo_url: p.photo_url || "",
      name: user.name || "", phone: user.phone || "", email: user.email || "",
    });
    setExequatur(p.exequatur || "");
  }, [user]);

  useEffect(() => {
    if (!user?.user_id) return;
    api.get("/availability", { params: { doctor_id: user.user_id } })
      .then((r) => setCenters(Array.from(new Set((r.data || []).map((b) => b.center_name).filter(Boolean)))))
      .catch(() => {});
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/doctors/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
      await refresh();
      toast.success("Foto actualizada");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.center_name) { toast.error("Selecciona un hospital de la lista"); return; }
    if (!form.specialty) { toast.error("Selecciona una especialidad"); return; }
    setBusy(true);
    try {
      await api.put("/doctors/me/profile", { ...form, price: Number(form.price) });
      await refresh();
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const status = user?.license_status || "pending";
  const banner = {
    approved: { icon: ShieldCheck, cls: "border-emerald-200 bg-emerald-50 text-emerald-600", label: "Licencia aprobada · perfil visible" },
    rejected: { icon: ShieldAlert, cls: "border-red-200 bg-red-50 text-red-500", label: "Licencia rechazada · contacta al administrador" },
  }[status];

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Mi perfil</h1>

      {banner && (
        <div className={`mt-4 flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold ${banner.cls}`} data-testid="license-status">
          <banner.icon className="h-4 w-4" /> {banner.label}
        </div>
      )}

      <form onSubmit={save} className="mt-6 space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E2E8F0] bg-slate-50">
            {form.photo_url ? (
              <img src={imgUrl(form.photo_url)} alt="Foto" className="h-full w-full object-cover" data-testid="profile-photo-preview" />
            ) : (
              <Camera className="h-6 w-6 text-slate-300" />
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadPhoto} data-testid="photo-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full border-[#E2E8F0] font-bold" data-testid="upload-photo-btn">
              <Upload className="mr-1.5 h-4 w-4" /> {uploading ? "Subiendo…" : "Subir foto"}
            </Button>
            <p className="mt-1.5 text-xs font-semibold text-slate-400">JPG, PNG o WEBP · máx 5MB</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nombre completo</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-name" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-phone" /></div>
        </div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Correo</Label><Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-email" /></div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Exequátur (no editable)</Label>
          <Input value={exequatur} readOnly disabled className="mt-1.5 rounded-xl border-[#E2E8F0] bg-slate-50 text-slate-500" data-testid="prof-exequatur" />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Especialidad *</Label>
          <div className="mt-1.5">
            <SpecialtyCombobox value={form.specialty} onSelect={(s) => setForm((f) => ({ ...f, specialty: s }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">No. Licencia</Label><Input required value={form.license_number} onChange={set("license_number")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-license" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Precio (RD$)</Label><Input required type="number" value={form.price} onChange={set("price")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-price" /></div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Hospital / Centro médico *</Label>
          <div className="mt-1.5">
            <HospitalCombobox value={form.center_name} onSelect={(h) => setForm((f) => ({ ...f, center_name: h.name, location: h.city }))} />
          </div>
          <p className="mt-1 text-xs font-semibold text-slate-400">Busca y selecciona un hospital de la lista (obligatorio).</p>
        </div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Ubicación</Label><Input required value={form.location} onChange={set("location")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="prof-location" /></div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Biografía</Label><Textarea value={form.bio} onChange={set("bio")} className="mt-1.5 min-h-24 rounded-xl border-[#E2E8F0]" data-testid="prof-bio" /></div>

        <div data-testid="my-centers">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mis centros (según disponibilidad)</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {centers.length === 0 ? (
              <p className="text-xs font-semibold text-slate-400">Aún no tienes centros. Agrega bloques en "Disponibilidad" para atender en uno o más hospitales.</p>
            ) : (
              centers.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-[#0284C7]" data-testid={`center-chip-${c}`}>
                  <MapPin className="h-3.5 w-3.5" /> {c}
                </span>
              ))
            )}
          </div>
        </div>

        <Button type="submit" disabled={busy} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="save-profile-btn">
          <IdCard className="mr-1.5 h-4 w-4" /> {busy ? "Guardando…" : "Guardar perfil"}
        </Button>
      </form>

      <div className="mt-6"><InsurancesEditor /></div>
    </div>
  );
}
